import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { SEOHead } from '../components/seo/SEOHead';
import { BreadcrumbSchema } from '../components/seo/SchemaMarkup';
import { Breadcrumbs } from '../components/seo/Breadcrumbs';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';
import Footer from '../components/Footer';

export default function Blog() {
  const navigate = useNavigate();

  const breadcrumbItems = [
    { name: 'Blog', url: null }
  ];

  const featuredPost = blogPosts.find(post => post.featured);
  const otherPosts = blogPosts.filter(post => post.id !== featuredPost?.id);

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <SEOHead
        title="Nightlife Blog | Jamshedpur Party & Club Guides | Scratch Unleashed"
        description="Expert guides on Jamshedpur nightlife, DJ events, club reviews, and party tips. Stay updated with the latest from Jamshedpur's premier nightlife destination."
        keywords="Jamshedpur nightlife blog, club guides Jamshedpur, DJ events Jamshedpur, party tips Jamshedpur, nightlife news Jamshedpur"
        canonical="https://scratchjsr.com/blog"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://scratchjsr.com/' },
        { name: 'Blog', url: 'https://scratchjsr.com/blog' }
      ]} />

      <div className="h-16"></div>

      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Header */}
        <div className="mb-12">
          <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl mb-4">
            NIGHTLIFE BLOG
          </h1>
          <p className="text-xl text-[var(--text-dim)]">
            Expert guides, tips, and insights on Jamshedpur's nightlife scene
          </p>
        </div>

        {/* Featured Post */}
        {featuredPost && (
          <Link
            to={`/blog/${featuredPost.slug}`}
            data-testid={`blog-featured-${featuredPost.slug}`}
            className="block"
          >
            <Card
              className="mb-12 overflow-hidden bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl cursor-pointer hover:border-[var(--accent-gold)] transition-all duration-300"
            >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              <div 
                className="h-64 lg:h-auto bg-cover bg-center"
                style={{ backgroundImage: `url(${featuredPost.image})` }}
              >
                <div className="h-full bg-gradient-to-t lg:bg-gradient-to-r from-[var(--surface)] to-transparent"></div>
              </div>
              <div className="p-8 flex flex-col justify-center">
                <Badge className="w-fit mb-3 text-xs">{featuredPost.category}</Badge>
                <h2 className="font-bebas text-3xl sm:text-4xl mb-3">
                  {featuredPost.title}
                </h2>
                <p className="text-[var(--text-dim)] mb-4 leading-relaxed">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center gap-4 text-sm text-[var(--text-muted)] mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar size={14} />
                    <span>{new Date(featuredPost.publishDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock size={14} />
                    <span>{featuredPost.readTime}</span>
                  </div>
                </div>
                <Button className="btn-crimson-glow rounded-xl w-fit">
                  Read Article
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
            </Card>
          </Link>
        )}

        {/* Other Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {otherPosts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              data-testid={`blog-card-${post.slug}`}
              className="block"
            >
              <Card
                className="h-full overflow-hidden bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl cursor-pointer hover:border-[var(--accent-gold)] transition-all duration-300"
              >
              <div 
                className="h-48 bg-cover bg-center"
                style={{ backgroundImage: `url(${post.image})` }}
              ></div>
              <div className="p-6">
                <Badge className="mb-3 text-xs">{post.category}</Badge>
                <h3 className="font-bebas text-2xl mb-2">
                  {post.title}
                </h3>
                <p className="text-sm text-[var(--text-dim)] mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                <div className="flex items-center gap-3 text-xs text-[var(--text-muted)]">
                  <div className="flex items-center gap-1">
                    <Calendar size={12} />
                    <span>{new Date(post.publishDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock size={12} />
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </div>
              </Card>
            </Link>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl p-8 text-center">
          <h2 className="font-bebas text-3xl mb-4">Experience Jamshedpur's Best Nightlife</h2>
          <p className="text-[var(--text-dim)] mb-6 max-w-2xl mx-auto">
            Ready to experience the venues we write about? Reserve your table at Scratch Unleashed and discover why we're Jamshedpur's premier nightlife destination.
          </p>
          <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
            Reserve Your Table
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
